import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { Importer } from '@feathr/blackbox';

import ImportMappingColumnCell from './ImportMappingColumnCell';
import ImportMappingFieldCell from './ImportMappingFieldCell';
import ImportMappingRemoveCell from './ImportMappingRemoveCell';
import ImportMappingSampleCell from './ImportMappingSampleCell';
import type { IImportMappingRow } from './ImportMappingTable.utils';

interface IColumnsOptions {
  t: TFunction;
  importer: Importer;
  disabled: boolean;
  /** Undefined until the first preview arrives, so a row doesn't claim its column is empty yet. */
  samples: Record<string, string> | undefined;
  /** Each column the latest preview could not read, against the value it refused. */
  unreadable: Map<string, string> | undefined;
}

export function importMappingColumns({
  t,
  importer,
  disabled,
  samples,
  unreadable,
}: IColumnsOptions): Array<ColumnDef<IImportMappingRow>> {
  return [
    {
      id: 'column',
      header: t('Column in your file'),
      meta: { isFlex: true, isPrimary: true },
      cell: ({ row }: CellContext<IImportMappingRow, unknown>): JSX.Element => (
        <ImportMappingColumnCell
          column={row.original.column}
          disabled={disabled}
          importer={importer}
        />
      ),
    },
    {
      id: 'sample',
      header: t('Sample value'),
      meta: { isFlex: true },
      cell: ({ row }: CellContext<IImportMappingRow, unknown>): JSX.Element | null => (
        <ImportMappingSampleCell
          sampleValue={samples ? samples[String(row.original.column.column_name)] ?? '' : null}
        />
      ),
    },
    {
      id: 'field',
      header: t('Feathr field'),
      meta: { isFlex: true },
      cell: ({ row }: CellContext<IImportMappingRow, unknown>): JSX.Element => (
        <ImportMappingFieldCell
          column={row.original.column}
          disabled={disabled}
          importer={importer}
          unreadableValue={unreadable?.get(String(row.original.column.column_name))}
        />
      ),
    },
    {
      // Not 'options': TanStackTable reserves that id for its own action menu column.
      id: 'remove',
      header: t('Options'),
      enableResizing: false,
      meta: { align: 'center' },
      cell: ({ row }: CellContext<IImportMappingRow, unknown>): JSX.Element => (
        <ImportMappingRemoveCell
          column={row.original.column}
          disabled={disabled}
          importer={importer}
        />
      ),
    },
  ];
}
